import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Api, type SupplierDto, type SupplierListParams, HttpClient, ContentType } from '../../generated';
import { API_BASE_URL, API_ENDPOINTS } from '../../config/api';

// Use generated API types
export type Supplier = SupplierDto;

// POST /api/Supplier/with-purchase-info response item
export interface SupplierWithPurchaseInfo {
  hasPurchaseHistory: boolean;
  /** 1 = en son alınan tedarikçi, 0 = değil */
  isLastSupplier?: number;
  id: number;
  cardCode?: string | null;
  companyName?: string | null;
  contactPerson?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  taxNumber?: string | null;
  paymentTerms?: string | null;
  currency?: string | null;
  inActive?: boolean;
  showPaymentTerm?: boolean;
  createdAt?: string | null;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  categories?: { categoryName?: string | null }[] | null;
}

// Supplier search request type
export type SupplierSearchRequest = SupplierListParams;

// Create API client instance compatible with generated API
const apiClient = new HttpClient({
  baseURL: API_BASE_URL,
  securityWorker: async () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      return {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
    }
    return {};
  },
  secure: true,
});

const api = new Api(apiClient);

interface SupplierState {
  loading: boolean;
  suppliers: Supplier[];
  selectedSupplier: Supplier | null;
  error: string | null;
  searchTerm: string;
  filters: {
    categoryId: number | null;
  };
  /** POST /api/Supplier/with-purchase-info sonucu */
  suppliersWithPurchaseInfo: SupplierWithPurchaseInfo[] | null;
  loadingWithPurchaseInfo: boolean;
  errorWithPurchaseInfo: string | null;
}

const initialState: SupplierState = {
  loading: false,
  suppliers: [],
  selectedSupplier: null,
  error: null,
  searchTerm: '',
  filters: {
    categoryId: null,
  },
  suppliersWithPurchaseInfo: null,
  loadingWithPurchaseInfo: false,
  errorWithPurchaseInfo: null,
};

// Async thunks for supplier operations
export const getSuppliers = createAsyncThunk(
  "supplier/getSuppliers",
  async (params: SupplierSearchRequest = {}, thunkAPI) => {
    
    try {
      
      
      // Generated API kullanımı
      const response = await api.api.supplierList(params);
      
      
      return response;
    } catch (error) {
      
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getSupplierById = createAsyncThunk(
  "supplier/getSupplierById",
  async (id: number, thunkAPI) => {
    
    try {
      
      
      // Generated API kullanımı
      const response = await api.api.supplierDetail({ id });
      
      
      return response;
    } catch (error) {
      
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createSupplier = createAsyncThunk(
  "supplier/createSupplier",
  async (supplierData: SupplierDto, thunkAPI) => {
    
    try {
      
      
      // Generated API kullanımı
      const response = await api.api.supplierCreate(supplierData);
      
      
      return response;
    } catch (error) {
      
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateSupplier = createAsyncThunk(
  "supplier/updateSupplier",
  async ({ supplierData }: { id: number; supplierData: SupplierDto }, thunkAPI) => {
    
    try {
      
      
      
      // Generated API kullanımı - update için create endpoint'ini kullanıyoruz
      // Eğer ayrı update endpoint'i varsa, o kullanılabilir
      const response = await api.api.supplierCreate(supplierData);
      
      
      return response;
    } catch (error) {
      
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteSupplier = createAsyncThunk(
  "supplier/deleteSupplier",
  async (id: number, thunkAPI) => {
    
    try {
      
      
      // Generated API kullanımı
      const response = await api.api.supplierDelete({ id });
      
      
      return { id, response };
    } catch (error) {
      
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const searchSuppliers = createAsyncThunk(
  "supplier/searchSuppliers",
  async (_searchTerm: string, thunkAPI) => {
    
    try {
      
      
      // Generated API kullanımı - şimdilik tüm supplierları getir, client-side filtreleme yapacağız
      const response = await api.api.supplierList({});
      
      
      return response;
    } catch (error) {
      
      return thunkAPI.rejectWithValue(error);
    }
  }
);

/** Parametreler: body (JSON body = tek string, örn. "abc"), opsiyonel categoryId (query) */
export interface GetSuppliersWithPurchaseInfoParams {
  body: string;
  categoryId?: number;
}

export const getSuppliersWithPurchaseInfo = createAsyncThunk(
  "supplier/getSuppliersWithPurchaseInfo",
  async ({ body, categoryId }: GetSuppliersWithPurchaseInfoParams, thunkAPI) => {
    try {
      // Backend schema: request body = JSON string (type: "string"). Gövde tırnaklı string olmalı: "abc"
      const response = await apiClient.request<any>({
        path: API_ENDPOINTS.SUPPLIER.WITH_PURCHASE_INFO,
        method: "POST",
        ...(categoryId != null && { query: { CategoryId: categoryId } }),
        body: JSON.stringify(body),
        type: ContentType.Json,
        secure: true,
      });
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const supplierSlice = createSlice({
  name: "supplier",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuppliers: (state) => {
      state.suppliers = [];
      state.error = null;
    },
    clearSelectedSupplier: (state) => {
      state.selectedSupplier = null;
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        categoryId: null,
      };
      state.searchTerm = '';
    },
    updateSupplierEmail: (state, action) => {
      const { index, email } = action.payload;
      if (state.suppliers && index >= 0 && index < state.suppliers.length) {
        const supplier = state.suppliers[index];
        if (supplier) {
          supplier.email = email;
        }
      }
    },
  },
  extraReducers(builder) {
    // Get Suppliers
    builder.addCase(getSuppliers.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getSuppliers.fulfilled, (state, action) => {
      state.loading = false;
      // Generated API response structure - data is directly the array or wrapped
      const responseData = action.payload;
      if (Array.isArray(responseData)) {
        state.suppliers = responseData;
      } else if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        // If response has data property
        const data = (responseData as any).data;
        if (Array.isArray(data)) {
          state.suppliers = data;
        } else if (data && data.suppliers) {
          state.suppliers = data.suppliers;
        }
      }
      state.error = null;
    });
    builder.addCase(getSuppliers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Tedarikçiler yüklenemedi';
    });

    // Get Supplier By ID
    builder.addCase(getSupplierById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getSupplierById.fulfilled, (state, action) => {
      state.loading = false;
      // Generated API response structure
      const responseData = action.payload;
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        state.selectedSupplier = (responseData as any).data;
      } else {
        state.selectedSupplier = responseData as Supplier;
      }
      state.error = null;
    });
    builder.addCase(getSupplierById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Tedarikçi detayları yüklenemedi';
    });

    // Create Supplier
    builder.addCase(createSupplier.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createSupplier.fulfilled, (state, action) => {
      state.loading = false;
      // Add the new supplier to the list
      const responseData = action.payload;
      let newSupplier: Supplier | null = null;
      
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        newSupplier = (responseData as any).data;
      } else {
        newSupplier = responseData as Supplier;
      }
      
      if (newSupplier) {
        state.suppliers.push(newSupplier);
      }
      state.error = null;
    });
    builder.addCase(createSupplier.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Tedarikçi oluşturulamadı';
    });

    // Update Supplier
    builder.addCase(updateSupplier.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateSupplier.fulfilled, (state, action) => {
      state.loading = false;
      // Update the supplier in the list
      const responseData = action.payload;
      let updatedSupplier: Supplier | null = null;
      
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        updatedSupplier = (responseData as any).data;
      } else {
        updatedSupplier = responseData as Supplier;
      }
      
      if (updatedSupplier) {
        const index = state.suppliers.findIndex(s => s.id === updatedSupplier!.id);
        if (index !== -1) {
          state.suppliers[index] = updatedSupplier;
        }
        if (state.selectedSupplier?.id === updatedSupplier.id) {
          state.selectedSupplier = updatedSupplier;
        }
      }
      state.error = null;
    });
    builder.addCase(updateSupplier.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Tedarikçi güncellenemedi';
    });

    // Delete Supplier
    builder.addCase(deleteSupplier.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteSupplier.fulfilled, (state, action) => {
      state.loading = false;
      // Remove the supplier from the list
      const { id } = action.payload;
      state.suppliers = state.suppliers.filter(s => s.id !== id);
      if (state.selectedSupplier?.id === id) {
        state.selectedSupplier = null;
      }
      state.error = null;
    });
    builder.addCase(deleteSupplier.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Tedarikçi silinemedi';
    });

    // Search Suppliers
    builder.addCase(searchSuppliers.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(searchSuppliers.fulfilled, (state, action) => {
      state.loading = false;
      // Generated API response structure - data is directly the array or wrapped
      const responseData = action.payload;
      if (Array.isArray(responseData)) {
        state.suppliers = responseData;
      } else if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        // If response has data property
        const data = (responseData as any).data;
        if (Array.isArray(data)) {
          state.suppliers = data;
        } else if (data && data.suppliers) {
          state.suppliers = data.suppliers;
        }
      }
      state.error = null;
    });
    builder.addCase(searchSuppliers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Tedarikçi arama başarısız';
    });

    // Get Suppliers With Purchase Info
    builder.addCase(getSuppliersWithPurchaseInfo.pending, (state) => {
      state.loadingWithPurchaseInfo = true;
      state.errorWithPurchaseInfo = null;
    });
    builder.addCase(getSuppliersWithPurchaseInfo.fulfilled, (state, action) => {
      state.loadingWithPurchaseInfo = false;
      const payload = action.payload as unknown;
      let list: SupplierWithPurchaseInfo[] = [];
      if (Array.isArray(payload)) {
        list = payload as SupplierWithPurchaseInfo[];
      } else if (payload && typeof payload === 'object' && 'data' in payload && Array.isArray((payload as { data: unknown }).data)) {
        list = (payload as { data: SupplierWithPurchaseInfo[] }).data;
      } else if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        list = [payload as SupplierWithPurchaseInfo];
      }
      state.suppliersWithPurchaseInfo = list;
      state.errorWithPurchaseInfo = null;
    });
    builder.addCase(getSuppliersWithPurchaseInfo.rejected, (state, action) => {
      state.loadingWithPurchaseInfo = false;
      state.errorWithPurchaseInfo = action.payload instanceof Error
        ? action.payload.message
        : 'Tedarikçi satın alma bilgisi yüklenemedi';
    });
  },
});

export const updateSupplierEmail = (index: number, email: string) => ({
  type: 'supplier/updateSupplierEmail',
  payload: { index, email }
});

export const { 
  clearError, 
  clearSuppliers, 
  clearSelectedSupplier, 
  setSearchTerm, 
  setFilters, 
  clearFilters 
} = supplierSlice.actions;
export default supplierSlice.reducer;
